import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      variant: {
        paid: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700',
        pending: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700',
        overdue: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-700',
        active: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-700',
        inactive: 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-600',
        info: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:ring-sky-700',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
)

const dotColors: Record<string, string> = {
  paid: 'bg-emerald-500',
  pending: 'bg-amber-500',
  overdue: 'bg-red-500',
  active: 'bg-primary-500',
  inactive: 'bg-gray-400',
  info: 'bg-sky-500',
}

export interface BadgeProps
  extends React.HTMLAttributes<'span'>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean
}

function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  const resolvedVariant = variant ?? 'info'
  return (
    <span
      className={cn(badgeVariants({ variant }), className)}
      {...(props as React.HTMLAttributes<HTMLSpanElement>)}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 rounded-full', dotColors[resolvedVariant])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  )
}

export { Badge, badgeVariants }
